import React from 'react';

import Link from '../../../../../../library/link/link';
import Typography from '../../../../../../library/typography/typography';
import Price from '../../../../../../components/price/price';
import ProductImage from '../../../../../../components/product-image/product-image';
import CompareCheckbox from '../../../../components/compare-checkbox/compare-checkbox';
import TopThreeTileHeader from '../top-three-tile-header/top-three-tile-header';

import { useProductQuantity } from '../../../../../../shared/hooks/hooks';

import {
  getNormalizedProductType,
  getPriceInfo,
  getProductImage
} from '../../../../../../shared/utils/product/product';

import {
  BundleType,
  ProductData,
  ProductTypeEnum
} from '../../../../../../shared/types/graphql-types';
import { PRODUCT_IMAGE_FORMATS } from '../../../../../../shared/constants/product-images';
import { PRODUCT_TILE_TYPES } from '../../../../../../shared/constants/treadwell';

import styles from './top-three-tile.scss';

interface TopThreeTileProps {
  onViewDetailsClick?: (value: string) => void;
  product: ProductData;
  type: (typeof PRODUCT_TILE_TYPES)[keyof typeof PRODUCT_TILE_TYPES];
}

//TODO: Attempt to use ProductTile to make TopThreeTile and SuggestedSellingTile consistent
function TopThreeTile(props: TopThreeTileProps) {
  const { onViewDetailsClick, product, type } = props;

  const {
    brand,
    mapDisplayRule,
    mapRuleSatisfied,
    name = '',
    rearProduct,
    recommendationData,
    unitName,
    url = ''
  } = product || {};

  const { rank } = recommendationData || {};
  const isTreadwell = Boolean(rank && rank > 0);

  const priceInfo = getPriceInfo(product);
  const rearPriceInfo = getPriceInfo(rearProduct);
  const { mapPrice, price, priceRange } = priceInfo || {};
  const {
    mapPrice: rearMapPrice,
    price: rearPrice,
    priceRange: rearPriceRange
  } = rearPriceInfo || {};
  const priceSubtext =
    mapRuleSatisfied &&
    mapDisplayRule?.messageDisplay &&
    mapDisplayRule?.mapPriceDisplay
      ? product.mapDisplayRule?.message
      : '';

  const rearCode = rearProduct?.code;

  const normalizedProductType =
    getNormalizedProductType(product) || ProductTypeEnum.Tires;
  const { quantity, rearQuantity } = useProductQuantity({
    articleServiceType: product?.articleServiceType,
    bundleType: product?.rearProduct ? BundleType.StaggeredSet : undefined,
    productType: normalizedProductType
  });

  const imageUrl = getProductImage(product, {
    format: PRODUCT_IMAGE_FORMATS.PRODUCT_X_SMALL,
    shouldFallBackToFirst: true
  })?.url;

  return (
    <div className={rearCode ? styles['staggered-tile'] : styles['tile']}>
      <div className={styles['inner-tile']}>
        <TopThreeTileHeader isTreadwell={isTreadwell} rank={rank} type={type} />
        <Link
          className={styles['container-link']}
          onClick={() =>
            onViewDetailsClick?.(
              `product_list_attribute_top_3_${type?.toLowerCase()}`
            )
          }
          to={url}
        >
          <div className={styles['tile-content']}>
            {imageUrl && !rearCode && (
              <ProductImage
                ariaLabel={name || ''}
                className={styles['product-image']}
                height={120}
                imageFormatType="PRODUCT_X_SMALL"
                imageUrl={imageUrl}
                width={120}
              />
            )}
            <div className={styles['text-container']}>
              <div className={styles['brand-name-container']}>
                <Typography
                  className={styles['brand']}
                  size="extraSmall"
                  tag="span"
                  weight="black"
                >
                  {brand}
                </Typography>
                <Typography
                  className={styles['name']}
                  tag="span"
                  weight="black"
                >
                  {name}
                </Typography>
              </div>
              <div className={styles['tile-summary']}>
                <div className={styles['price-compare-container']}>
                  <span className={styles['price-container']}>
                    <Typography
                      className={
                        rearCode ? styles['staggered-price'] : styles['price']
                      }
                      size="small"
                      tag="span"
                    >
                      {rearCode && (
                        <span className={styles['price-label']}>Front:</span>
                      )}
                      {price && (
                        <Price
                          className="price"
                          mapPrice={mapPrice}
                          price={price}
                          priceRange={priceRange}
                          unitName={unitName}
                        />
                      )}
                    </Typography>
                    {rearCode && rearPrice && (
                      <Typography
                        className={styles['staggered-price-rear']}
                        size="small"
                        tag="span"
                      >
                        <span className={styles['price-label']}>Rear:</span>
                        <Price
                          className="price"
                          mapPrice={rearMapPrice}
                          price={rearPrice}
                          priceRange={rearPriceRange}
                          unitName={unitName}
                        />
                      </Typography>
                    )}
                  </span>

                  {!rearProduct && (
                    <CompareCheckbox
                      hideSelectMoreText
                      product={product}
                      quantity={quantity}
                      rearQuantity={rearQuantity}
                    />
                  )}
                </div>
                {priceSubtext && (
                  <Typography
                    className={styles['price-subtext']}
                    size="extraSmall"
                    tag="span"
                  >
                    {priceSubtext}
                  </Typography>
                )}

                <div className={styles['details-container']}>
                  {url && (
                    <Typography
                      className={styles['view-details']}
                      size="small"
                      tag="span"
                      weight="bold"
                    >
                      View Details
                    </Typography>
                  )}

                  {rearProduct && (
                    <CompareCheckbox
                      hideSelectMoreText
                      product={product}
                      quantity={quantity}
                      rearQuantity={rearQuantity}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}

export default TopThreeTile;
